import { useTranslation } from 'react-i18next'
import { invalidateComponentsCache } from '../../../api/components'
import { invalidateConnectedPositionCache } from '../../../api/connectedPositions'
import { invalidateMooringCache, invalidateMooringMapCaches } from '../../../api/moorings'
import {
  invalidatePositionCache,
  useGetConnectedPositionsForDropDown,
  useGetPositionsWithMinimalData,
  useRegenerateLocalityMap,
  useUpdatePosition,
} from '../../../api/positions'
import { ShowDialogProps } from '../../../show-dialog'
import { PositionForm } from './components/PositionsForm'
import { toast } from 'react-toastify'
import { useCallback, useMemo } from 'react'
import { IPosition } from '../../../generated-types'
import { invalidateLocalityCache, patchCageStatusCache } from 'src/api/localities'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMainComponentCache } from 'src/api/main-component'

interface UpdatePositionProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  position: IPosition
}
export const UpdatePositionDialog: React.FC<UpdatePositionProps> = ({
  pop,
  customerId,
  localityId,
  mooringId,
  positionId,
  position,
}) => {
  const { t } = useTranslation(['position', 'common'])
  const { data: positionsList } = useGetConnectedPositionsForDropDown(customerId, localityId, mooringId)

  const { data: mooringPositions } = useGetPositionsWithMinimalData(customerId, localityId, mooringId, {
    allPositions: true,
    includeHidden: true,
  })
  const activePositions = useMemo(() => mooringPositions ?? [], [mooringPositions])

  const { mutateAsync: updatePositionAsync, isLoading } = useUpdatePosition(
    customerId,
    localityId,
    mooringId,
    position?.id as EntityId,
  )
  const { mutateAsync: regenerateMap } = useRegenerateLocalityMap(customerId as EntityId, localityId as EntityId)

  const onSubmit = useCallback(
    async form => {
      await updatePositionAsync(form, {
        onSuccess: async () => {
          invalidateConnectedPositionCache.getConnectedPositions(
            customerId as EntityId,
            mooringId as EntityId,
            localityId as EntityId,
          )
          invalidatePositionCache.getPositions(customerId, localityId, mooringId)
          invalidatePositionCache.getPositionsWithMinimalData(customerId, localityId, mooringId)
          invalidateComponentsCache.getComponents(customerId as EntityId, positionId as EntityId)
          invalidateMooringCache.getGetMooring(customerId as EntityId, localityId as EntityId, mooringId as EntityId, {
            isWithAllInfo: true,
          })
          invalidateMooringCache.getMapSvg(customerId as EntityId, localityId as EntityId, mooringId as EntityId)
          invalidateLocalityCache.getCageStatus(customerId, localityId as EntityId)
          patchCageStatusCache(customerId, localityId, position?.id as EntityId, {
            name: form.name,
            reference: form.reference,
          })
          invalidateMooringCache.getMooringsForMap(customerId as EntityId, localityId)
          invalidateMooringMapCaches(customerId, localityId, mooringId)
          invalidatePositionCache.getPosition(
            customerId as EntityId,
            localityId as EntityId,
            mooringId as EntityId,
            position?.id as EntityId,
          )
          invalidatePositionCache.getPositionsForDropdownByMooring(customerId, localityId, mooringId)
          invalidatePositionCache.getCagesForDropdownByMooring(customerId, localityId, mooringId)
          invalidatePositionCache.getPositionsForDropDownByMooringId(customerId, localityId, mooringId)
          invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId as EntityId)
          invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId)
          invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId as EntityId)
          invalidateMooringCache.getAllPositionGroups(customerId, localityId, mooringId)
          invalidateMooringCache.getPositionGroupsWithDrawing(customerId, localityId, mooringId)
          invalidateMooringCache.getPositionGroupsForDrawing(customerId, localityId, mooringId)
          invalidateMainComponentCache.getMainComponentById(customerId)
          invalidatePositionCache.getConnectedPositionsForDropdown(customerId, localityId, mooringId)
          invalidatePositionCache.getComponentsForPositions(customerId, localityId)
          invalidatePositionCache.getComponentsByProductId(customerId, localityId)

          toast.success(t('updatedSuccessfully', { ns: 'common' }))
          pop()
          regenerateMap({
            onSuccess: () => {},
            onError: () => {},
          })
        },
      })
    },
    [customerId, localityId, mooringId, pop, positionId, t, updatePositionAsync, position, regenerateMap],
  )

  return (
    <>
      {position && (
        <PositionForm
          onSubmit={onSubmit}
          onCancel={() => pop()}
          initialValues={position}
          loading={isLoading}
          positions={positionsList}
          customerId={customerId}
          localityId={localityId}
          title={t('updatePosition', { ns: 'position' })}
          isDialog={true}
          activePositions={activePositions}
        />
      )}
    </>
  )
}
